import React from 'react';
import { dateFormatter } from '../../utils/utils';
import { PERMISSIONS } from './permissionMeta';
import PermissionGrantChip from './PermissionGrantChip';

/**
 * The OS-permission grant matrix. Wide, horizontally-scrolling table with:
 *   - fixed identity columns (No, Name, Platform, app versions)
 *   - one column per *visible* permission, each cell a ✓/✕/– chip
 *   - last-updated timestamps
 * The wrapper carries `up-table-wrap` so the matrix scrolls inside its own box
 * (both axes) and the header row can pin to the top — see user-permissions.css.
 * Server-side sortable headers (Name / Platform / Last updated / No).
 *
 * Props: { users, page, visibleColumns, sortField, sortOrder, onSort }
 */
const SORTABLE = {
  user_id: 'No',
  name: 'Name',
  platform: 'Platform',
  last_updated_mobile: 'Last updated (Mobile)',
  last_updated_watch: 'Last updated (Watch)',
};

const SortTh = ({ field, label, sortField, sortOrder, onSort }) => {
  const active = sortField === field;
  const arrow = active ? (sortOrder === 'DESC' ? ' ▼' : ' ▲') : '';
  return (
    <th
      className="au-th-sort"
      role="button"
      tabIndex={0}
      aria-sort={active ? (sortOrder === 'DESC' ? 'descending' : 'ascending') : 'none'}
      onClick={() => onSort(field)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSort(field); } }}
    >
      {label}{arrow}
    </th>
  );
};

const PermissionMatrixTable = ({ users = [], page = 1, visibleColumns, sortField = 'id', sortOrder = 'ASC', onSort }) => {
  const cols = PERMISSIONS.filter((p) => !visibleColumns || visibleColumns.includes(p.key));

  return (
    <div className="au-table-wrap up-table-wrap">
      <table className="au-table up-matrix">
        <thead>
          <tr>
            <SortTh field="user_id" label={SORTABLE.user_id} sortField={sortField} sortOrder={sortOrder} onSort={onSort} />
            <SortTh field="name" label={SORTABLE.name} sortField={sortField} sortOrder={sortOrder} onSort={onSort} />
            <SortTh field="platform" label={SORTABLE.platform} sortField={sortField} sortOrder={sortOrder} onSort={onSort} />
            <th>Mobile app version</th>
            <th>Watch app version</th>
            {cols.map((c) => <th key={c.key}>{c.label}</th>)}
            <SortTh field="last_updated_mobile" label={SORTABLE.last_updated_mobile} sortField={sortField} sortOrder={sortOrder} onSort={onSort} />
            <SortTh field="last_updated_watch" label={SORTABLE.last_updated_watch} sortField={sortField} sortOrder={sortOrder} onSort={onSort} />
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td data-label="No" className="tnum">{u.user_id}</td>
              <td data-label="Name" className="up-ellipsis">{u.name}</td>
              <td data-label="Platform">{u.platform}</td>
              {/* Label fix: Mobile column shows the MOBILE version, Watch shows the WATCH version. */}
              <td data-label="Mobile app version">{u.mobile_app_version}</td>
              <td data-label="Watch app version">{u.watch_app_version}</td>
              {cols.map((c) => (
                <td key={c.key} data-label={c.label} className="up-chip-cell">
                  <PermissionGrantChip value={u[c.key]} />
                </td>
              ))}
              <td data-label="Last updated (Mobile)" className="tnum">{u.last_updated_mobile ? dateFormatter(u.last_updated_mobile) : '–'}</td>
              <td data-label="Last updated (Watch)" className="tnum">{u.last_updated_watch ? dateFormatter(u.last_updated_watch) : '–'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PermissionMatrixTable;
